/**
 * useApiForm — vee-validate + zod wrapper for our drawer/modal forms.
 *
 * Two behaviours that matter for UX correctness:
 *
 * 1. validateOnMount: false (load-bearing).
 *    vee-validate v4 already defaults to false, but we set it
 *    explicitly because Manus's union-type fix in Batch B brought
 *    the schema in via `computed`, which can re-run mount-time
 *    validation in some edge cases. Belt and braces.
 *
 * 2. Errors are GATED on user interaction.
 *    Pristine forms (drawer just opened, user hasn't typed anything)
 *    do not show errors. Errors surface when EITHER:
 *      - the field has been touched (focused and blurred), OR
 *      - the user has attempted to submit at least once.
 *    This is exposed as `form.errors` (the override) so templates
 *    don't need to change. The raw vee-validate errors are still
 *    available as `form.rawErrors` for testing/debugging.
 *
 * NOTE: This file is a drop-in replacement. The public surface
 * (return shape) is unchanged from a consumer's point of view —
 * `form.errors.value.fieldName` keeps working, just gated.
 */
import { computed } from 'vue'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import type { z } from 'zod'
import { ApiException } from '@/api/types'

export interface UseApiFormOptions<TSchema extends z.ZodType, TResponse> {
  schema: TSchema
  initialValues: z.input<TSchema>
  submit: (values: z.output<TSchema>) => Promise<TResponse>
  fieldNameMap?: Record<string, string>
  onSuccess?: (response: TResponse) => void
  onError?: (error: unknown) => void
}

export function useApiForm<TSchema extends z.ZodType, TResponse>(
  opts: UseApiFormOptions<TSchema, TResponse>,
) {
  // ---------------------------------------------------------------
  // vee-validate setup
  // ---------------------------------------------------------------
  const form = useForm<z.input<TSchema>>({
    validationSchema: toTypedSchema(opts.schema),
    initialValues: opts.initialValues as any,
    // EXPLICIT: do not validate on mount. Required for clean drawer-open UX.
    validateOnMount: false,
    // Don't keep stale field values across mounts.
    keepValuesOnUnmount: false,
  })

  const isSubmitting = computed(() => form.isSubmitting.value)
  const submitError = computed(() => {
    // Vee-validate's submitCount increments on attempts; we don't
    // surface a separate ref, callers use the form's own state.
    return null
  })

  // ---------------------------------------------------------------
  // Gated error display.
  //
  // Returns vee-validate's errors map ONLY when the user has either
  // attempted submission or touched at least one field. Before that,
  // errors are hidden so a freshly-opened drawer never shows red text
  // before the user has done anything.
  //
  // After submitCount > 0 OR meta.touched is true, all errors show
  // (mirroring vee-validate's native behaviour). This means typing in
  // one field will surface validation hints across the form — which
  // is the correct progressive-disclosure UX: "the user has engaged,
  // help them complete the form."
  // ---------------------------------------------------------------
  const shouldShowErrors = computed(
    () => form.submitCount.value > 0 || form.meta.value.touched,
  )

  const displayErrors = computed<Partial<Record<string, string>>>(() =>
    shouldShowErrors.value ? form.errors.value : {},
  )

  /**
   * Explicit accessor — equivalent to `form.errors.value[name]` but
   * with the gating applied. Useful for any caller that wants to be
   * unambiguous about which errors variant they're reading.
   */
  function errorFor(name: string): string | undefined {
    return displayErrors.value[name]
  }

  // ---------------------------------------------------------------
  // submit() — wraps vee-validate's handleSubmit with our async
  // try/catch + ApiException → field-error mapping.
  // ---------------------------------------------------------------
  const submit = form.handleSubmit(async (values) => {
    try {
      const response = await opts.submit(values as z.output<TSchema>)
      opts.onSuccess?.(response)
      return response
    } catch (err) {
      if (err instanceof ApiException && err.fieldErrors.length > 0) {
        for (const fe of err.fieldErrors) {
          const mappedField = opts.fieldNameMap?.[fe.field] ?? fe.field
          form.setFieldError(mappedField as any, fe.message)
        }
      }
      opts.onError?.(err)
      throw err
    }
  })

  return {
    // vee-validate native surface (spread first so we can override).
    ...form,

    // Override `errors` so consumers that read `form.errors.value.X`
    // automatically get gated behaviour. No template changes needed.
    errors: displayErrors,

    // Raw, ungated errors — vee-validate's native ref. Use only for
    // testing or advanced UI (e.g. a "show all problems" debug panel).
    rawErrors: form.errors,

    // Convenience.
    errorFor,
    isSubmitting,
    submitError,

    // Replace bare handleSubmit with our wrapped submit().
    submit,
  }
}
